import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { keccak256, parseAbi, type Address, type Hex, type PublicClient } from 'viem';
import { z } from 'zod';
import { terminalRouterVersionForDecoder } from './terminalRouterVersion.js';
import { assertDirectNativeWrapperRouterCompatibility, directNativeWrapperRouterWeth9Offsets,
  observeDirectNativeWrapperAtBlock, readSelectedDirectNativeWrapperObservationProfile,
  type DirectNativeWrapperObservation, type DirectNativeWrapperObservationProfile } from './directNativeWrapperObservation.js';

const UINT256_MAX = (1n << 256n) - 1n;
const addressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/)
  .transform(value => value.toLowerCase() as Address);
const nonzeroAddressSchema = addressSchema.refine(value => !/^0x0{40}$/.test(value));
const hashSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/)
  .refine(value => !/^0x0{64}$/i.test(value))
  .transform(value => value.toLowerCase() as Hex);
const uint256Schema = z.bigint().min(0n).max(UINT256_MAX);
const pinSchema = z.strictObject({ account: nonzeroAddressSchema, codeHash: hashSchema });
const emptyCodeHash = keccak256('0x');
type SupportedChainId = 4663 | 46630;
const legacyProfileSchemaForChain = <ChainId extends SupportedChainId>(chainId: ChainId) => z.strictObject({
  schemaVersion: z.literal(1),
  profile: z.literal('reviewed-aeweth-transparent-proxy-v1'),
  chainId: z.literal(chainId),
  router: pinSchema,
  wrapper: pinSchema,
  implementation: pinSchema,
  admin: z.discriminatedUnion('kind', [
    pinSchema.extend({ kind: z.literal('contract') }),
    pinSchema.extend({ kind: z.literal('owned-local-empty-code') }),
  ]),
  expectedL2Gateway: nonzeroAddressSchema,
  // The retained initializer permits a zero L1 counterpart. It is still an
  // explicit caller-reviewed identity, never inferred from wrapper supply.
  expectedL1Address: addressSchema,
});
const profileSchemaForChain = <ChainId extends SupportedChainId>(chainId: ChainId) => {
  const legacy = legacyProfileSchemaForChain(chainId);
  return z.discriminatedUnion('schemaVersion', [legacy, legacy.extend({
    schemaVersion: z.literal(2),
    requiredDecoderVersion: z.literal('5.12.0'),
    routerVersion: z.literal('2.1.2'),
  })]);
};
const sourceSchema = z.strictObject({
  number: uint256Schema,
  hash: hashSchema,
  timestamp: uint256Schema,
});
const policySchema = z.strictObject({
  maxAgeSeconds: z.number().int().safe().min(1),
  maxFutureSeconds: z.number().int().safe().min(0),
});

export type LegacyNativeWrapperObservationProfile<ChainId extends SupportedChainId = typeof SELECTED_ACCOUNT_CHAIN_ID> =
  z.infer<ReturnType<typeof profileSchemaForChain<ChainId>>>;
export type NativeWrapperObservationProfile<ChainId extends SupportedChainId = typeof SELECTED_ACCOUNT_CHAIN_ID> =
  LegacyNativeWrapperObservationProfile<ChainId> | DirectNativeWrapperObservationProfile<ChainId>;
export type NativeWrapperObservationClient = Pick<PublicClient,
  'getStorageAt' | 'getBytecode' | 'readContract' | 'getBalance' | 'getBlock' | 'getChainId'>;

export const nativeWrapperObservationAbi = parseAbi([
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',
  'function l2Gateway() view returns (address)',
  'function l1Address() view returns (address)',
]);

const implementationSlot = '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc';
const adminSlot = '0xb53127684a568b3173ae13b9f8a6016e243e63b6e8ee1178d6a717850b5d6103';
// Router 2.1.1 retained Sourcify artifact SHA256
// ffe65cb3ffe793a4a24303669d1fa2dc21966c00ff1869cba6ed32a22b33b52e.
// WETH9 is internal immutable, not an external getter. These are its three
// compiler reference offsets, each immediately after a PUSH32 opcode.
// Router 2.1.2 retained Sourcify artifact SHA256
// 6f56bf07f203fb2ad7ea4d3ee8ef86a963953939395239879d5f437fe67078cb.
// Reproduced compiler output/AST associates WETH9 node 2379 with these
// references. The reviewed profile schema, never caller-supplied offsets or
// compatible bytecode, chooses the version-specific association.
const weth9Offsets = {
  1: [6779, 12249, 12585],
  2: [6749, 12200, 12536],
} as const;
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** Shape checks do not establish source-review provenance. The caller must
 * independently select/review this exact aeWETH/proxy/router profile; there
 * are no default live addresses or code pins. Matching admin code does not
 * verify its owner/control. Empty admin code is allowed only for an explicitly
 * owned-local fixture, not a live governance claim. */
function readProfileForChain<ChainId extends SupportedChainId>(
  input: unknown, chainId: ChainId,
): NativeWrapperObservationProfile<ChainId> {
  // Distinct source family, not a proxy profile with invented upgrade fields.
  if (input !== null && typeof input === 'object' && 'profile' in input
    && input.profile === 'reviewed-solmate-direct-weth-v1') {
    return readSelectedDirectNativeWrapperObservationProfile(input, chainId);
  }
  const profile = profileSchemaForChain(chainId).parse(structuredClone(input));
  const roles = [profile.router.account, profile.wrapper.account,
    profile.implementation.account, profile.admin.account];
  if (new Set(roles).size !== roles.length) throw new Error('Native wrapper profile role collision');
  if ((profile.admin.kind === 'owned-local-empty-code') !== same(profile.admin.codeHash, emptyCodeHash)) {
    throw new Error('Native wrapper profile empty admin code requires explicit owned-local classification');
  }
  for (const pin of [profile.router, profile.wrapper, profile.implementation]) {
    if (same(pin.codeHash, emptyCodeHash)) throw new Error('Native wrapper profile requires nonempty runtime');
  }
  return profile;
}

export function readNativeWrapperObservationProfile(input: unknown): NativeWrapperObservationProfile {
  return readProfileForChain(input, SELECTED_ACCOUNT_CHAIN_ID);
}

/** Archived 4663 data only. This reader does not change the selected observer
 * domain or authorize an RPC read, signature or transaction. */
export function readLegacyNativeWrapperObservationProfile(input: unknown): NativeWrapperObservationProfile<4663> {
  return readProfileForChain(input, 4663);
}

/** Bind an already-admitted wrapper profile to the caller's captured decoder
 * policy. Matching runtime offsets alone cannot select execution authority. */
export function assertNativeWrapperRouterCompatibility(
  profile: NativeWrapperObservationProfile<SupportedChainId>, requiredDecoderVersion: string,
): void {
  if (profile.profile === 'reviewed-solmate-direct-weth-v1') {
    return assertDirectNativeWrapperRouterCompatibility(profile, requiredDecoderVersion);
  }
  const routerVersion = terminalRouterVersionForDecoder(requiredDecoderVersion);
  if (profile.schemaVersion === 1 && requiredDecoderVersion === '5.11.3' && routerVersion === '2.1.1') return;
  if (profile.schemaVersion === 2 && profile.requiredDecoderVersion === '5.12.0'
    && profile.routerVersion === '2.1.2' && requiredDecoderVersion === profile.requiredDecoderVersion
    && routerVersion === profile.routerVersion) return;
  throw new Error('Native wrapper profile router version mismatch');
}

/** Code-owned association shared by live preparation and historical settlement.
 * The profile must already have been admitted; this is not a deployment pin. */
export function nativeWrapperRouterWeth9Offsets(
  profile: NativeWrapperObservationProfile<SupportedChainId>, requiredDecoderVersion: string,
): readonly number[] {
  if (profile.profile === 'reviewed-solmate-direct-weth-v1') {
    return directNativeWrapperRouterWeth9Offsets(profile, requiredDecoderVersion);
  }
  assertNativeWrapperRouterCompatibility(profile, requiredDecoderVersion);
  return Object.freeze([...weth9Offsets[profile.schemaVersion]]);
}

/** Code-owned execution roles only; direct wrappers have no implementation/admin. */
export function nativeWrapperExecutionRoles(profile: NativeWrapperObservationProfile<SupportedChainId>): readonly Address[] {
  return Object.freeze(profile.profile === 'reviewed-solmate-direct-weth-v1'
    ? [profile.wrapper.account]
    : [profile.wrapper.account, profile.implementation.account, profile.admin.account]);
}

/** Whether the admitted source family has a proxy upgrade-review prerequisite.
 * This never verifies or grants authority; direct WETH has no such role. */
export function nativeWrapperUpgradeAuthorityVerificationRequired(profile: NativeWrapperObservationProfile<SupportedChainId>): boolean {
  return profile.profile !== 'reviewed-solmate-direct-weth-v1';
}

export type LegacyNativeWrapperObservation = {
  schemaVersion: 1;
  chainId: typeof SELECTED_ACCOUNT_CHAIN_ID;
  profile: LegacyNativeWrapperObservationProfile;
  source: { number: bigint; hash: Hex; timestamp: bigint };
  requiredDeadlineUnixSeconds: bigint;
  observedAtUnixSeconds: bigint;
  routerWeth9: Address;
  weth9ImmutableReferences: { offset: number; length: 32; word: Hex }[];
  implementationSlotWord: Hex;
  adminSlotWord: Hex;
  decimals: 18;
  l2Gateway: Address;
  l1Address: Address;
  totalSupply: bigint;
  nativeCollateral: bigint;
  nativeSurplus: bigint;
  upgradeAuthorityVerified: false;
  submissionAuthorized: false;
};
export type NativeWrapperObservation = LegacyNativeWrapperObservation | DirectNativeWrapperObservation;

/** Read-only prerequisite, not execution, treasury settlement or governance
 * approval. Use the existing createCanonicalBlockReadClient client for
 * EIP-1898 hash-bound state reads; a bare numbered/latest RPC transport is
 * not a substitute. Independently select the source hash in the reviewed
 * server/fixture, not from an untrusted trading request. This mandatory
 * selected source is checked before and after the reads. A current
 * collateral/supply observation says nothing
 * about future upgrades/backing or actual deposit/withdraw execution. */
export async function observeNativeWrapperAtBlock(input: {
  client: NativeWrapperObservationClient;
  profile: NativeWrapperObservationProfile<SupportedChainId>;
  source: { number: bigint; hash: Hex; timestamp: bigint };
  policy: { maxAgeSeconds: number; maxFutureSeconds: number };
  requiredDeadlineUnixSeconds: bigint;
  now?: () => number;
}): Promise<NativeWrapperObservation> {
  // Dispatch synchronously; the selected observer captures input/methods before
  // its first await and retains the same caller-owned canonical read budget.
  const selected = readNativeWrapperObservationProfile(input.profile);
  if (selected.profile === 'reviewed-solmate-direct-weth-v1') {
    return observeDirectNativeWrapperAtBlock({ ...input, profile: selected });
  }
  // Capture the actual runtime object, all methods and the clock before any
  // await. Later input/client mutations cannot renew the original review.
  const client = input.client;
  const getStorageAt = client.getStorageAt.bind(client);
  const getBytecode = client.getBytecode.bind(client);
  const readContract = client.readContract.bind(client);
  const getBalance = client.getBalance.bind(client);
  const getBlock = client.getBlock.bind(client);
  const getChainId = client.getChainId.bind(client);
  const now = input.now ?? Date.now;
  const profile = selected;
  const source = sourceSchema.parse(structuredClone(input.source));
  const policy = policySchema.parse(structuredClone(input.policy));
  const deadline = uint256Schema.min(1n).parse(input.requiredDeadlineUnixSeconds);
  let lastTime = -1;
  function fresh() {
    const time = now();
    if (!Number.isSafeInteger(time) || time < 0 || time < lastTime) {
      throw new Error('Native wrapper observation invalid clock');
    }
    lastTime = time;
    const seconds = BigInt(Math.floor(time / 1000));
    if (seconds >= deadline) throw new Error('Native wrapper observation review expired');
    if (seconds > source.timestamp + BigInt(policy.maxAgeSeconds)
      || source.timestamp > seconds + BigInt(policy.maxFutureSeconds)) {
      throw new Error('Native wrapper observation stale or future source');
    }
    return seconds;
  }
  async function checked<T>(read: () => Promise<T>): Promise<T> {
    fresh();
    let result: T;
    try { result = await read(); }
    catch { throw new Error('Native wrapper observation read failed'); }
    fresh();
    return result;
  }
  const headerSchema = z.object({ number: uint256Schema, hash: hashSchema,
    parentHash: hashSchema, timestamp: uint256Schema });
  async function header() {
    const block = await checked(() => getBlock({ blockNumber: source.number, includeTransactions: false }));
    const parsed = headerSchema.parse(block);
    if (parsed.number !== source.number || !same(parsed.hash, source.hash)
      || parsed.timestamp !== source.timestamp) throw new Error('Native wrapper observation source mismatch');
    return parsed;
  }
  async function chain() {
    if (await checked(() => getChainId()) !== profile.chainId) {
      throw new Error('Native wrapper observation chain mismatch');
    }
  }
  async function runtime(pin: { account: Address; codeHash: Hex }, allowEmpty = false) {
    const observed = await checked(() => getBytecode({ address: pin.account, blockNumber: source.number }));
    const code = observed ?? '0x';
    if (!/^0x(?:[0-9a-fA-F]{2})*$/.test(code) || (!allowEmpty && code === '0x')
      || !same(keccak256(code), pin.codeHash)) throw new Error('Native wrapper observation runtime mismatch');
    return code;
  }
  function slotAddress(word: Hex | undefined, expected: Address) {
    if (!word || !/^0x0{24}[0-9a-fA-F]{40}$/.test(word)
      || !same(`0x${word.slice(-40)}`, expected)) throw new Error('Native wrapper observation proxy slot mismatch');
    return word;
  }

  fresh();
  await chain();
  const initialHeader = await header();
  const routerCode = await runtime(profile.router);
  const expectedWord = `0x${'0'.repeat(24)}${profile.wrapper.account.slice(2)}` as Hex;
  const references = nativeWrapperRouterWeth9Offsets(profile,
    profile.schemaVersion === 1 ? '5.11.3' : profile.requiredDecoderVersion).map(offset => {
    const start = 2 + offset * 2;
    const word = `0x${routerCode.slice(start, start + 64)}` as Hex;
    if (routerCode.slice(start - 2, start).toLowerCase() !== '7f' || !same(word, expectedWord)) {
      throw new Error('Native wrapper observation router WETH9 association mismatch');
    }
    return { offset, length: 32 as const, word };
  });
  await runtime(profile.wrapper);
  const implementationWord = slotAddress(await checked(() => getStorageAt({
    address: profile.wrapper.account, slot: implementationSlot, blockNumber: source.number,
  })), profile.implementation.account);
  const adminWord = slotAddress(await checked(() => getStorageAt({
    address: profile.wrapper.account, slot: adminSlot, blockNumber: source.number,
  })), profile.admin.account);
  await runtime(profile.implementation);
  await runtime(profile.admin, profile.admin.kind === 'owned-local-empty-code');
  const decimals = await checked(() => readContract({ address: profile.wrapper.account,
    abi: nativeWrapperObservationAbi, functionName: 'decimals', blockNumber: source.number }));
  if (decimals !== 18) throw new Error('Native wrapper observation decimals mismatch');
  const l2Gateway = addressSchema.parse(await checked(() => readContract({ address: profile.wrapper.account,
    abi: nativeWrapperObservationAbi, functionName: 'l2Gateway', blockNumber: source.number })));
  const l1Address = addressSchema.parse(await checked(() => readContract({ address: profile.wrapper.account,
    abi: nativeWrapperObservationAbi, functionName: 'l1Address', blockNumber: source.number })));
  if (!same(l2Gateway, profile.expectedL2Gateway) || !same(l1Address, profile.expectedL1Address)) {
    throw new Error('Native wrapper observation initialized identity mismatch');
  }
  const totalSupply = uint256Schema.parse(await checked(() => readContract({ address: profile.wrapper.account,
    abi: nativeWrapperObservationAbi, functionName: 'totalSupply', blockNumber: source.number })));
  const nativeCollateral = uint256Schema.parse(await checked(() => getBalance({
    address: profile.wrapper.account, blockNumber: source.number,
  })));
  if (nativeCollateral < totalSupply) throw new Error('Native wrapper observation underbacked supply');
  const finalHeader = await header();
  if (!same(finalHeader.parentHash, initialHeader.parentHash)) {
    throw new Error('Native wrapper observation source parent mismatch');
  }
  await chain();
  const observedAtUnixSeconds = fresh();
  return { schemaVersion: 1, chainId: SELECTED_ACCOUNT_CHAIN_ID, profile, source,
    requiredDeadlineUnixSeconds: deadline, observedAtUnixSeconds,
    routerWeth9: profile.wrapper.account, weth9ImmutableReferences: references,
    implementationSlotWord: implementationWord, adminSlotWord: adminWord,
    decimals: 18, l2Gateway, l1Address, totalSupply, nativeCollateral,
    nativeSurplus: nativeCollateral - totalSupply,
    upgradeAuthorityVerified: false, submissionAuthorized: false };
}
